import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { ref, update } from 'firebase/database';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AuditUpdateWorkOrder = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { regData, workData } = location.state || {};

    const [formData, setFormData] = useState({
        schemeName: '',
        tenderAmount: '',
        gst: '',
        insurance: '',
        totalAmount: '',
        workingPeriod: '३ महिने',
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (workData) {
            const initialTender = parseFloat(workData.tenderAmount) || 0;
            const initialGst = parseFloat(workData.gst) || Math.round(initialTender * 0.18);
            const initialInsurance = parseFloat(workData.insurance) || 2097;
            const total = initialTender + initialGst + initialInsurance;

            setFormData({
                schemeName: workData.schemeName || regData?.scheme || 'जि.प.खीय निधी शिक्षण शाळा दुरुस्ती',
                tenderAmount: initialTender,
                gst: initialGst,
                insurance: initialInsurance,
                totalAmount: total.toFixed(2),
                workingPeriod: workData.workingPeriod || '३ महिने',
            });
        }
    }, [workData, regData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let updatedFormData = { ...formData, [name]: value };

        if (name === 'tenderAmount' || name === 'gst' || name === 'insurance') {
            const tender = parseFloat(updatedFormData.tenderAmount) || 0;
            const gst = parseFloat(updatedFormData.gst) || 0;
            const insurance = parseFloat(updatedFormData.insurance) || 0;
            updatedFormData.totalAmount = (tender + gst + insurance).toFixed(2);
        }

        setFormData(updatedFormData);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!workData || !workData.id) {
            toast.error('Work reference ID missing.');
            return;
        }

        setLoading(true);
        try {
            const meetingNum = workData.meetingNumber || "05/2025-2026";
            const workRef = ref(db, `ekamwatapzp/WorkNames/${meetingNum}/${workData.id}`);

            await update(workRef, {
                schemeName: formData.schemeName,
                tenderAmount: formData.tenderAmount,
                gst: formData.gst,
                insurance: formData.insurance,
                totalAmount: formData.totalAmount,
                workingPeriod: formData.workingPeriod,
                isWorkOrderUpdated: true,
                updatedAt: new Date().toISOString()
            });

            toast.success('वर्क ऑर्डर यशस्वीरित्या अपडेट केली!');
            setTimeout(() => navigate(-1), 1500);
        } catch (error) {
            console.error("Error updating work order:", error);
            toast.error('वर्क ऑर्डर अपडेट करताना त्रुटी आली.');
        } finally {
            setLoading(false);
        }
    };

    const goBack = () => navigate(-1);

    if (!workData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-[#f4f7f6] p-4 font-marathi">
                <div className="bg-white p-8 rounded border border-gray-200 shadow-sm text-center max-w-md">
                    <h2 className="text-xl font-bold text-red-600 mb-4">माहिती उपलब्ध नाही</h2>
                    <p className="mb-6 text-gray-500 font-medium">कृपया यादीतून पुन्हा प्रयत्न करा.</p>
                    <button 
                        onClick={goBack}
                        className="bg-[#5c6bc0] text-white px-8 py-2 rounded hover:bg-[#3f51b5] transition font-semibold"
                    >
                        परत जा
                    </button>
                </div>
            </div>
        );
    }

    const applicantName = regData?.applicantName || regData?.name || regData?.fullName || "अर्जदाराचे नाव दिलेले नाही";
    const workName = workData.workName || "कामाचे नाव दिलेले नाही";

    return (
        <div className="min-h-screen bg-[#f4f7f6] p-4 md:p-8 font-marathi">
            <ToastContainer position="top-right" autoClose={3000} />
            
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 px-2">
                <div className="flex items-center gap-4">
                    <h1 className="text-xl font-bold text-gray-800 tracking-tight">अपडेट वर्क ऑर्डर</h1>
                </div>
                <div className="flex items-center gap-2 text-[12px] font-medium text-gray-400">
                    सांगली जिल्हा परिषद / <span className="text-gray-600">अपडेट वर्क ऑर्डर</span>
                </div>
            </div>

            {/* Form Card */}
            <div className="bg-white rounded border border-gray-200 shadow-sm p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                    <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-1">अर्जाचे नाव</h3>
                        <p className="text-sm text-gray-600">{applicantName}</p>
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-1">कामाचे नाव</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{workName}</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Scheme Name */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                लेखाशिर्ष <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="schemeName"
                                value={formData.schemeName}
                                onChange={handleChange}
                                className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#5c6bc0] transition text-gray-800"
                                required
                            >
                                <option value="जि.प.खीय निधी शिक्षण शाळा दुरुस्ती">जि.प.खीय निधी शिक्षण शाळा दुरुस्ती</option>
                                <option value="जिल्हा वार्षिक योजना">जिल्हा वार्षिक योजना</option>
                                <option value="इतर योजना">इतर योजना</option>
                            </select>
                        </div>
                        
                        {/* Tender Amount */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                निविदा स्विकृती र.रु. <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                name="tenderAmount"
                                value={formData.tenderAmount}
                                onChange={handleChange}
                                className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#5c6bc0] transition text-gray-800"
                                required
                            />
                        </div>

                        {/* GST */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                GST <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                name="gst"
                                value={formData.gst}
                                onChange={handleChange}
                                className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#5c6bc0] transition text-gray-800"
                                required
                            />
                        </div>

                        {/* Insurance */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                विमा <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                name="insurance"
                                value={formData.insurance}
                                onChange={handleChange}
                                className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#5c6bc0] transition text-gray-800"
                                required
                            />
                        </div>

                        {/* Total Amount (Read-only visually distinct) */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                एकूण र.रु. <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="totalAmount"
                                value={formData.totalAmount}
                                readOnly
                                className="w-full bg-gray-100 border border-gray-300 rounded px-3 py-2 text-sm text-gray-600 font-semibold cursor-not-allowed"
                            />
                        </div>

                        {/* Working Period */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                कामाची मुदत <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="workingPeriod"
                                value={formData.workingPeriod}
                                onChange={handleChange}
                                className="w-full bg-gray-100 border border-gray-300 rounded px-3 py-2 text-sm text-gray-800"
                                required
                            />
                        </div>
                    </div>

                    <div className="pt-2">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`bg-[#5c6bc0] hover:bg-[#3f51b5] text-white px-8 py-2 rounded text-sm font-semibold transition shadow-sm ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {loading ? 'Submitting...' : 'Submit'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AuditUpdateWorkOrder;
